import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { PageHeaderComponent } from '@shared/components';
import { ImportHistoryDetail, ImportHistoryItem, PortalMemberImportService } from '@core/api/services/portal-member-import.service';
import { timeout } from 'rxjs/operators';

@Component({
  selector: 'app-portal-import-history',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatCardModule, MatTableModule, PageHeaderComponent],
  templateUrl: './portal-import-history.component.html',
  styleUrl: './portal-import-history.component.scss',
})
export class PortalImportHistoryComponent implements OnInit {
  @ViewChild('detailPanel') private detailPanel?: ElementRef<HTMLElement>;
  readonly displayedColumns = ['fileName', 'importedAtUtc', 'created', 'existing', 'blocked', 'omitted', 'memberCount', 'actions'];
  batches: ImportHistoryItem[] = [];
  selected?: ImportHistoryDetail;
  sourceJson?: string;
  loading = false;
  detailLoading = false;
  sourceLoading = false;
  sourceCopied = false;
  error?: string;

  constructor(private readonly api: PortalMemberImportService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = undefined;
    this.api.getHistory().subscribe({
      next: batches => {
        this.batches = batches;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: error => this.fail(error, 'No se pudo cargar el historial de importaciones.'),
    });
  }

  select(batch: ImportHistoryItem): void {
    if (this.detailLoading) return;
    if (this.selected?.batch.id === batch.id) {
      this.selected = undefined;
      this.sourceJson = undefined;
      return;
    }
    this.detailLoading = true;
    this.selected = undefined;
    this.sourceJson = undefined;
    this.api.getHistoryDetail(batch.id).pipe(timeout(15000)).subscribe({
      next: detail => {
        this.selected = detail;
        this.detailLoading = false;
        this.cdr.markForCheck();
        requestAnimationFrame(() => this.detailPanel?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      },
      error: error => this.fail(error, 'No se pudo cargar el detalle del lote.'),
    });
  }

  isSelected(batch: ImportHistoryItem): boolean {
    return this.selected?.batch?.id === batch.id;
  }

  toggleSourceJson(): void {
    if (!this.selected || this.sourceLoading) return;
    if (this.sourceJson !== undefined) {
      this.sourceJson = undefined;
      this.sourceCopied = false;
      this.cdr.markForCheck();
      return;
    }

    this.sourceLoading = true;
    this.sourceCopied = false;
    this.api.getHistorySourceJson(this.selected.batch.id).pipe(timeout(15000)).subscribe({
      next: source => {
        this.sourceJson = source.sourceJson;
        this.sourceLoading = false;
        this.cdr.markForCheck();
      },
      error: error => this.fail(error, 'No se pudo cargar el JSON de origen.'),
    });
  }

  async copySourceJson(): Promise<void> {
    if (!this.sourceJson || !navigator.clipboard) return;
    await navigator.clipboard.writeText(this.sourceJson);
    this.sourceCopied = true;
    this.cdr.markForCheck();
  }

  private fail(error: unknown, fallback: string): void {
    const response = error as { error?: { detail?: string; title?: string }; message?: string };
    this.error = response.error?.detail || response.error?.title || response.message || fallback;
    this.loading = false;
    this.detailLoading = false;
    this.sourceLoading = false;
    this.cdr.markForCheck();
  }
}
